import { apiClient } from '@/services/apiClient';

const unwrap = (r: any) => r.data.data;

export interface GoogleAdsStatus {
  configured: boolean;
  apiBase: string;
  oauthSource: string;
  clientId: { configured: boolean; projectNumber: string | null; fingerprint: string | null };
  clientSecret: { configured: boolean; fingerprint: string | null };
  developerToken: { configured: boolean; fingerprint: string | null };
  hint: string;
}

/**
 * Diagnostic — config shape + GCP project number from the OAuth client_id so
 * DEVELOPER_TOKEN_PROHIBITED mismatches are obvious.
 *
 * CORP-SUPER-ADMIN ONLY (JwtAuth + EnsureSuperAdmin). It was public, and it
 * returned secret lengths and the developer token's last 4 characters; both are
 * gone, replaced by a truncated SHA-256 `fingerprint` for comparing deploys.
 * Callers must handle 401/403 — nothing calls this today.
 */
export async function getGoogleAdsStatus(): Promise<GoogleAdsStatus> {
  const res = await apiClient.get<{ data: GoogleAdsStatus }>('/integrations/google-ads/status');
  return res.data.data;
}

export interface GoogleAdsDiagnose {
  env: { clientId: boolean; clientSecret: boolean; developerToken: boolean; projectNumber: string | null };
  account: { hasRow: boolean; hasRefreshToken: boolean; customerId: string | null; loginCustomerId: string | null };
  probe: null | {
    accessToken: { ok: boolean; error: string | null };
    listAccessibleCustomers: null | { status: number; ok: boolean; error: string | null; body: string };
  };
  hint: string | null;
}

/**
 * Authed deep diagnostic — actually runs the failing oauth refresh +
 * listAccessibleCustomers call and returns the raw HTTP status / response.
 */
export async function diagnoseGoogleAds(): Promise<GoogleAdsDiagnose> {
  const res = await apiClient.get<{ data: GoogleAdsDiagnose }>('/admin/marketing/integrations/google_ads/diagnose');
  return res.data.data;
}

export type MarketingProvider =
  | 'google_analytics' | 'google_ads' | 'meta_ads' | 'google_search_console' | 'zernio'
  // Browser tracking pixels. Separate from the ads-API providers above:
  // those blobs are rewritten by the OAuth/connector flows, which would
  // wipe a hand-entered pixel id.
  | 'meta_pixel' | 'google_ads_pixel';

export type ConnectionStatus = 'not_connected' | 'needs_selection' | 'connected';

export interface SelectedEntity {
  /** propertyId | customerId | adAccountId | siteUrl */
  key: string;
  id: string | null;
  label: string | null;
}

export interface MarketingIntegrationRow {
  provider: MarketingProvider;
  connectionStatus: ConnectionStatus;
  configured: boolean;
  status: 'active' | 'disabled' | 'error';
  lastSyncAt: string | null;
  lastError: string | null;
  selected: SelectedEntity | null;
  /** Best-effort identity of the OAuth user who authorized us. */
  connectedAs: string | null;
  /**
   * Pixel providers only: the saved ids, echoed back so the form can show what
   * is configured. Safe to return — a pixel id ships in the page to every
   * visitor. No other provider's credentials appear here.
   */
  publicValues?: Record<string, string>;
}

export interface PickableAccount {
  id: string;
  label: string;
  /** Google Ads only: the MCC (manager) id that manages this account, or null
   *  for direct accounts. Must be passed back on select so reporting queries
   *  send the correct login-customer-id. */
  loginCustomerId?: string | null;
}

export function listMarketingIntegrations(): Promise<MarketingIntegrationRow[]> {
  return apiClient.get('/admin/marketing/integrations').then(unwrap);
}

export function deleteMarketingIntegration(
  provider: MarketingProvider,
): Promise<MarketingIntegrationRow> {
  return apiClient.delete(`/admin/marketing/integrations/${provider}`).then(unwrap);
}

export function testMarketingIntegration(
  provider: MarketingProvider,
): Promise<{ ok: boolean; note?: string }> {
  return apiClient.post(`/admin/marketing/integrations/${provider}/test`).then(unwrap);
}

/** Returns the provider's authorize URL for the FE to redirect/popup to. */
export function startOAuth(
  provider: MarketingProvider,
): Promise<{ authorizeUrl: string }> {
  return apiClient.get(`/admin/marketing/oauth/${provider}/start`).then(unwrap);
}

/** Lists available properties / customers / ad accounts after OAuth completes. */
export function listPickableAccounts(
  provider: MarketingProvider,
): Promise<{ accounts: PickableAccount[] }> {
  return apiClient.get(`/admin/marketing/integrations/${provider}/accounts`).then(unwrap);
}

/** Saves the user's pick. */
export function selectAccount(
  provider: MarketingProvider,
  id: string,
  label?: string,
  loginCustomerId?: string | null,
): Promise<MarketingIntegrationRow> {
  return apiClient
    .post(`/admin/marketing/integrations/${provider}/select`, { id, label, loginCustomerId })
    .then(unwrap);
}

/** Manual credential save — used for non-OAuth providers (Zernio, the tracking pixels). */
export function upsertMarketingIntegration(
  provider: MarketingProvider,
  credentials: Record<string, unknown>,
): Promise<MarketingIntegrationRow> {
  return apiClient.put(`/admin/marketing/integrations/${provider}`, credentials).then(unwrap);
}

// ─── Ads Dashboard aggregator ─────────────────────────────────────────────

export interface GaStats {
  configured: boolean;
  status: 'active' | 'disabled' | 'error';
  sessions: number;
  users: number;
  conversions: number;
  bounceRate: number;
  period: string;
}

export interface GoogleAdsStats {
  configured: boolean;
  status: 'active' | 'disabled' | 'error';
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  ctr: number;
  cpc: number;
  period: string;
}

export interface MetaAdsStats extends GoogleAdsStats {}

export interface AdsDashboard {
  google_analytics: GaStats;
  google_ads: GoogleAdsStats;
  meta_ads: MetaAdsStats;
}

export function getAdsDashboard(): Promise<AdsDashboard> {
  return apiClient.get('/marketing/ads/dashboard').then(unwrap);
}

export interface AdsTrendPoint {
  date: string;
  spend: number;
  conversions: number;
  sessions: number;
  googleSpend: number;
  metaSpend: number;
  cpa: number | null;
}

export interface AdsTrend {
  startDate: string;
  endDate: string;
  rows: AdsTrendPoint[];
  sources: { google_analytics: boolean; google_ads: boolean; meta_ads: boolean };
}

export function getAdsTrend(days = 30): Promise<AdsTrend> {
  return apiClient.get('/marketing/ads/trend', { params: { days } }).then(unwrap);
}

export interface AdsCampaign {
  id: string;
  name: string;
  channel: 'Google Ads' | 'Meta Ads';
  status: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  cpa: number | null;
  roas: number | null;
}

export interface AdsCampaignsResult {
  startDate: string;
  endDate: string;
  rows: AdsCampaign[];
  errors: Partial<Record<'google_ads' | 'meta_ads', string>>;
}

export function getAdsCampaigns(): Promise<AdsCampaignsResult> {
  return apiClient.get('/marketing/ads/campaigns').then(unwrap);
}

// ─── Provider display metadata ────────────────────────────────────────────

export const PROVIDER_LABELS: Record<MarketingProvider, string> = {
  google_analytics: 'Google Analytics 4',
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  google_search_console: 'Google Search Console',
  zernio: 'Zernio',
  meta_pixel: 'Meta Pixel',
  google_ads_pixel: 'Google Ads Conversion Tag',
};

export const PROVIDER_HELP: Record<MarketingProvider, string> = {
  google_analytics:
    'Sign in with Google and pick the GA4 property you want to track. We pull sessions, users, conversions and bounce rate.',
  google_ads:
    'Sign in with Google and pick the Ads customer to report on. We pull spend, clicks, impressions and conversions.',
  meta_ads:
    'Sign in with Meta and pick the ad account to report on. We pull spend, engagement and conversions.',
  google_search_console:
    'Sign in with Google and pick the verified site you want to track. We pull top queries, top pages, clicks, impressions and average position.',
  zernio:
    'Paste your Zernio API key to publish, schedule and queue social posts across every account you connected in Zernio — Twitter/X, Instagram, Facebook, LinkedIn, TikTok, YouTube, Threads, Bluesky, Reddit, Pinterest and Telegram.',
  meta_pixel:
    'Paste the Meta Pixel ID for this account so page views and conversions are attributed to it.',
  google_ads_pixel:
    'Paste the Google Ads conversion ID (the AW- prefixed one) for this account, and the conversion label if you use one.',
};

export const PICK_LABEL: Record<MarketingProvider, string> = {
  google_analytics: 'Pick a GA4 property',
  google_ads: 'Pick an Ads customer',
  meta_ads: 'Pick an ad account',
  google_search_console: 'Pick a verified site',
  zernio: '—',
  // Not account pickers: both are an ID typed into TrackingPixelsCard.
  meta_pixel: '—',
  google_ads_pixel: '—',
};

// ─── SEO dashboard ────────────────────────────────────────────────────────

export interface SeoOrganicStats {
  ok: boolean;
  error?: string;
  startDate?: string;
  endDate?: string;
  sessions?: number;
  users?: number;
  conversions?: number;
  bounceRate?: number;
  engagedSessions?: number;
}

export interface SeoQueryRow {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface SeoPageRow {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface SeoGscStats {
  ok: boolean;
  error?: string;
  siteUrl?: string;
  startDate?: string;
  endDate?: string;
  totalClicks?: number;
  totalImpressions?: number;
  avgCtr?: number;
  avgPosition?: number;
  topQueries?: SeoQueryRow[];
  topPages?: SeoPageRow[];
}

export interface SeoDashboard {
  organic: SeoOrganicStats;
  gsc: SeoGscStats;
}

export function getSeoDashboard(days = 30): Promise<SeoDashboard> {
  return apiClient.get('/marketing/seo/dashboard', { params: { days } }).then(unwrap);
}

// ─── Command Center aggregate ─────────────────────────────────────────────

export interface ChannelGroupRow {
  channel: string;
  sessions: number;
  users: number;
  conversions: number;
  engagedSessions: number;
}

export interface ChannelGroupsResult {
  ok: boolean;
  error?: string;
  startDate?: string;
  endDate?: string;
  rows?: ChannelGroupRow[];
}

export type ContentCategory = 'blog' | 'guide' | 'pricing' | 'product' | 'landing' | 'other';

export interface ContentPageRow {
  path: string;
  category: ContentCategory;
  sessions: number;
  engagementRate: number;
  avgDuration: number;
  conversions: number;
  pageViews: number;
}

export interface ContentPagesResult {
  ok: boolean;
  error?: string;
  startDate?: string;
  endDate?: string;
  rows?: ContentPageRow[];
}

export interface CommandCenter {
  dashboard: AdsDashboard;
  trend: AdsTrend | null;
  campaigns: AdsCampaignsResult | null;
  organic: SeoOrganicStats;
  gsc: SeoGscStats;
  channelGroups: ChannelGroupsResult;
  contentPages: ContentPagesResult;
  days: number;
  /** ISO code all connected ad accounts report in; null when unknown or mixed. */
  currency: string | null;
}

/** Reporting periods the command center accepts, in days. */
export const REPORT_PERIODS = [30, 90, 180, 365] as const;
export type ReportPeriod = (typeof REPORT_PERIODS)[number];

export function getCommandCenter(days: ReportPeriod = 30): Promise<CommandCenter> {
  return apiClient.get('/marketing/command-center', { params: { days } }).then(unwrap);
}

/** "last 30 days" / "last 12 months" for the period a response covers. */
export function periodLabel(days: number): string {
  return days === 365 ? 'last 12 months' : `last ${days} days`;
}
